import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { HelpCircle } from 'lucide-react';

/**
 * Reference popover for the Keep / Merge / Delete / Defer per-row actions
 * used during Bulk Import staging review. Mounted in two places:
 *   1. BulkSimilarityModal title bar
 *   2. BulkImportReviewPage header
 *
 * Pure presentational — no API calls, no state.
 */
const StagingActionsHelpPopover = ({ testId = 'staging-actions-help-btn', align = 'start' }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 text-gray-400 hover:text-indigo-600"
        data-testid={testId}
        aria-label="Action reference"
      >
        <HelpCircle className="w-4 h-4" />
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-[420px] text-xs" align={align}>
      <div className="space-y-2">
        <div className="font-semibold text-sm text-gray-800">What each action does</div>
        <table className="w-full text-[11px] border-collapse">
          <thead>
            <tr className="border-b text-gray-500 text-left">
              <th className="py-1.5 pr-2 font-medium w-14">Action</th>
              <th className="py-1.5 pr-2 font-medium">What it does to the staging row</th>
              <th className="py-1.5 font-medium w-14">DB effect on Apply</th>
            </tr>
          </thead>
          <tbody className="align-top">
            <tr className="border-b">
              <td className="py-1.5 pr-2 font-medium text-gray-700">Keep</td>
              <td className="py-1.5 pr-2 text-gray-600">
                <strong>Promotes</strong> the staging row straight to the live questions DB. Use when the row is a genuinely
                unique question that needs no merge — Apply pushes it to production and clears it from staging.
              </td>
              <td className="py-1.5 text-gray-700 font-medium">Writes</td>
            </tr>
            <tr className="border-b bg-emerald-50/40">
              <td className="py-1.5 pr-2 font-medium text-emerald-700">Merge</td>
              <td className="py-1.5 pr-2 text-gray-600">
                Combines the staging row into <em>either an approved DB question</em> (green link) <em>or another cross-file staging row</em> (blue link). The surveyor + sitting becomes another
                {' '}<code className="text-[10px] bg-emerald-100 px-0.5">asked_by_history</code> entry on the target,
                then the source staging row is deleted. Pick one match above the threshold first.
              </td>
              <td className="py-1.5 text-emerald-700 font-medium">Writes</td>
            </tr>
            <tr className="border-b bg-red-50/40">
              <td className="py-1.5 pr-2 font-medium text-red-700">Delete</td>
              <td className="py-1.5 pr-2 text-gray-600">
                <strong>Soft-deletes</strong> the staging row on Apply — lands in <em>Staging Trash</em> for 7 days
                and can be restored back to staging (with its original filename &amp; batch) before it auto-purges.
                Use for wrong / duplicate / junk rows that should never enter the DB.
              </td>
              <td className="py-1.5 text-red-700 font-medium">Deletes</td>
            </tr>
            <tr className="bg-amber-50/40">
              <td className="py-1.5 pr-2 font-medium text-amber-700">Defer</td>
              <td className="py-1.5 pr-2 text-gray-600">
                Puts the row on hold — paints it amber so you can come back to it in a later triage session. Nothing happens on Apply. This is also the effective state of any row you leave <em>untagged</em>.
              </td>
              <td className="py-1.5 text-gray-400">None</td>
            </tr>
          </tbody>
        </table>
        <div className="text-[10px] text-gray-500 pt-1 border-t">
          Tip: clicking <strong>Apply</strong> commits Keep-promotions, Merges, and Deletes. The number in the button is
          {' '}<code className="bg-gray-100 px-0.5">keep + merge + delete</code> count. Defer and untagged rows are always left alone.
        </div>
      </div>
    </PopoverContent>
  </Popover>
);

export default StagingActionsHelpPopover;
